import {
  ActionIcon,
  Button,
  Group,
  Popover,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import { ANONYMOUS_REFINEMENT_LIMIT } from '../utils/anonymous-refinement-storage'

type AccountMode = 'anon' | 'signedIn'

function explanationBody(mode: AccountMode) {
  if (mode === 'anon') {
    return (
      <Text size="sm" c="dimmed">
        When your answers change the plan in a meaningful way, we update steps, time, tools, or
        cost. Each of those changes uses a free customization while you browse without an
        account. Sign up free to keep tailoring this plan and save it to your account.
      </Text>
    )
  }
  return (
    <Text size="sm" c="dimmed">
      We count the same kind of meaningful plan updates from your answers. With an account,
      there is no five-and-stop limit for these Q&amp;A customizations on this guide. Premium
      AI plan edits (natural language) are tracked separately when that feature applies.
    </Text>
  )
}

interface AnonymousRefinementBarProps {
  count: number
  /** Anonymous path: positive phase copy (no numeric &quot;of 5&quot;). Signed-in: running total. */
  display: 'capped' | 'total'
  /** Popover copy for anonymous vs signed-in. */
  accountMode: AccountMode
  /**
   * @default ANONYMOUS_REFINEMENT_LIMIT (only for {@link display} &quot;capped&quot;)
   */
  limit?: number
  /**
   * Slightly more padding for the customize sheet; header uses compact single row.
   */
  density?: 'compact' | 'comfortable'
  withInfo?: boolean
  /** Count 4: soft &quot;Save your progress&quot; (e.g. mark intent + sign-up). */
  onSaveProgress?: () => void
  /** Count 5: medium sign-up action. */
  onSignUp?: () => void
  /** Count 5: medium sign-in action. */
  onSignIn?: () => void
}

/**
 * Q&amp;A customization usage: one row, optional info. Anonymous uses positive phase copy;
 * signed-in uses a running total.
 */
export function AnonymousRefinementBar({
  count,
  display,
  accountMode,
  limit = ANONYMOUS_REFINEMENT_LIMIT,
  density = 'compact',
  withInfo = true,
  onSaveProgress,
  onSignUp,
  onSignIn,
}: AnonymousRefinementBarProps) {
  if (display === 'total') {
    const n = Math.max(0, count)
    return (
      <div data-testid="anonymous-refinement-summary" data-display="total">
        <Group
          gap={density === 'comfortable' ? 'md' : 'sm'}
          align="center"
          justify="space-between"
          wrap="nowrap"
          w="100%"
          py={density === 'comfortable' ? 2 : 0}
        >
          <Text
            size="xs"
            c="dimmed"
            data-testid="anonymous-refinement-label"
            style={{ flexShrink: 1, minWidth: 0 }}
            lineClamp={1}
          >
            {n} {n === 1 ? 'customization' : 'customizations'} on this plan
          </Text>
          {withInfo && (
            <Popover width={320} position="bottom-end" withArrow shadow="md">
              <Popover.Target>
                <ActionIcon
                  type="button"
                  variant="subtle"
                  color="gray"
                  size="sm"
                  radius="xl"
                  aria-label="What counts as a customization?"
                  style={{ flexShrink: 0 }}
                >
                  <IconInfoCircle size={16} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown p="md">
                <Stack gap="xs" id="customizations-explanation">
                  <Text size="sm" fw={600} mb="xs">
                    What counts as a customization?
                  </Text>
                  {explanationBody(accountMode)}
                </Stack>
              </Popover.Dropdown>
            </Popover>
          )}
        </Group>
      </div>
    )
  }

  const safeCount = Math.min(limit, Math.max(0, count))
  let phase: 'invite' | 'shape' | 'nudge' | 'limit' = 'invite'
  if (safeCount >= limit) phase = 'limit'
  else if (safeCount === 4) phase = 'nudge'
  else if (safeCount >= 1) phase = 'shape'

  const headline =
    phase === 'invite'
      ? 'Customize your plan ↓'
      : phase === 'shape'
        ? 'Your plan is taking shape ✨'
        : phase === 'nudge'
          ? 'One more customization available'
          : "You've built your plan! Save it before you go"

  return (
    <div
      data-testid="anonymous-refinement-summary"
      data-display="capped"
      data-phase={phase}
    >
      <Stack gap={density === 'comfortable' ? 'xs' : 4}>
        <Group
          gap={density === 'comfortable' ? 'md' : 'sm'}
          align="flex-start"
          justify="space-between"
          wrap="nowrap"
          w="100%"
          py={density === 'comfortable' ? 2 : 0}
        >
          <Text
            size="xs"
            c="dimmed"
            data-testid="anonymous-refinement-label"
            style={{ flex: 1, minWidth: 0 }}
          >
            {headline}
          </Text>
          {withInfo && (
            <Popover width={320} position="bottom-end" withArrow shadow="md">
              <Popover.Target>
                <ActionIcon
                  type="button"
                  variant="subtle"
                  color="gray"
                  size="sm"
                  radius="xl"
                  aria-label="What counts as a customization?"
                  style={{ flexShrink: 0 }}
                >
                  <IconInfoCircle size={16} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown p="md">
                <Stack gap="xs" id="customizations-explanation">
                  <Text size="sm" fw={600} mb="xs">
                    What counts as a customization?
                  </Text>
                  {explanationBody(accountMode)}
                </Stack>
              </Popover.Dropdown>
            </Popover>
          )}
        </Group>

        {phase === 'nudge' && onSaveProgress && (
          <UnstyledButton
            type="button"
            onClick={onSaveProgress}
            style={{ alignSelf: 'flex-start' }}
          >
            <Text size="xs" c="primary" td="underline" component="span">
              Save your progress
            </Text>
          </UnstyledButton>
        )}

        {phase === 'limit' && onSignUp && onSignIn && (
          <Group gap="sm" wrap="wrap">
            <Button size="sm" onClick={onSignUp}>
              Sign up
            </Button>
            <Button size="sm" variant="default" onClick={onSignIn}>
              Sign in
            </Button>
          </Group>
        )}
      </Stack>
    </div>
  )
}
